import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { REPORT_TYPE } from 'constants/constantVariables';
import TableComponent from 'components/shared/TableComponent';
import MonitoringSetup from './MonitoringSetup';
import './monitoringBulkSetup.scss';

// Bulk monitoring: select many businesses in the table, then set up a schedule for all of them at once using
// the same unified MonitoringSetup component as the per-application drawer (mode="bulk"). Per-report cadence
// and an explicit start/end window are applied across every selected business.

// Report TYPES offered in bulk (no per-owner granularity — businesses differ). Per-check credit prices are
// used for the client-side cost estimate; the backend bills each business by its own available reports.
const BULK_REPORTS = [
    { key: 'SOS', reportType: REPORT_TYPE.SOS, label: 'Secretary of State', credits: 1, available: true },
    { key: 'FMCSA', reportType: REPORT_TYPE.FMCSA, label: 'FMCSA', credits: 1, available: true },
    { key: 'BG', reportType: REPORT_TYPE.LEXIS_BACKGROUND, label: 'Background', credits: 4, available: true },
    { key: 'VER', reportType: REPORT_TYPE.LEXIS_VERIFICATION, label: 'Verification', credits: 3, available: true },
    { key: 'UCC', reportType: REPORT_TYPE.UCC, label: 'UCC search', credits: 2, available: true },
];

// Friendly short labels for the report-type enum names the API returns (table pills).
const REPORT_LABEL = {
    [REPORT_TYPE.SOS]: 'SOS',
    [REPORT_TYPE.FMCSA]: 'FMCSA',
    [REPORT_TYPE.LEXIS_BACKGROUND]: 'Background',
    [REPORT_TYPE.LEXIS_VERIFICATION]: 'Verification',
    [REPORT_TYPE.UCC]: 'UCC',
};
const reportLabel = (value) => REPORT_LABEL[value] || value;

const KNOWN_CADENCES = ['HOURLY', 'EVERY_6_HOURS', 'DAILY', 'WEEKLY', 'EVERY_2_WEEKS', 'EVERY_1_MONTH',
    'EVERY_2_MONTHS', 'EVERY_3_MONTHS', 'EVERY_4_MONTHS', 'EVERY_6_MONTHS', 'EVERY_12_MONTHS'];

// The config DTO's reports carry no id, so identify each report by reportType + entity (unique per row).
const singleReportKey = (r) => `${r.reportType}::${r.entityId || ''}`;

// Per-report display label for a single business's config (owner-scoped reports name their entity), mirroring
// the per-application drawer.
const singleReportLabel = (r) => {
    if (r.reportType === REPORT_TYPE.UCC) return `UCC search (${r.entityName})`;
    if (r.reportType === REPORT_TYPE.LEXIS_VERIFICATION) return 'Verification';
    if (r.reportType === REPORT_TYPE.LEXIS_BACKGROUND) return `Background Owner (${r.entityName})`;
    if (r.reportType === REPORT_TYPE.FMCSA) return 'FMCSA';
    if (r.reportType === REPORT_TYPE.SOS) return 'Secretary of State';
    return r.reportType;
};

const MonitoringBulkSetup = ({
    configs = [],
    headers = {},
    onQuery,
    onPreview,
    onApply,
    getConfig,
    updateConfig,
    onApplied,
    getDevEnabled,
    onRunNow,
    readOnly = false,
}) => {
    const [selected, setSelected] = useState(() => new Set());
    const [dialogOpen, setDialogOpen] = useState(false);
    const [applying, setApplying] = useState(false);
    const [balance, setBalance] = useState(null);
    // Whether this client is on the credit system. A non-credit client (no balance record → null balance) sees
    // NO credit/cost information anywhere in monitoring; the feature works the same for them. Defaults to true
    // (the common case) and is corrected on mount, so credit clients never lose the column while it loads.
    const [creditsApplicable, setCreditsApplicable] = useState(true);
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('ALL'); // ALL | ON | OFF
    // When exactly one business is selected we load its full config and edit it faithfully (per-report
    // cadence, window, real prices, per-entity reports) via the per-app endpoint — so opening it reflects
    // reality and applying never downgrades an existing schedule. Multiple businesses use the bulk endpoint.
    const [singleConfig, setSingleConfig] = useState(null);
    const [loadingConfig, setLoadingConfig] = useState(false);
    const [applyError, setApplyError] = useState(null);
    // Dev/local-only "run monitoring now" — hidden unless the backend reports dev tools are enabled.
    const [devEnabled, setDevEnabled] = useState(false);
    const [running, setRunning] = useState(false);
    const [runMsg, setRunMsg] = useState(null);

    const page = Number(headers['x-pagination-current-page-num'] || 0);
    const totalPages = Number(headers['x-pagination-total-pages'] || 0);
    const totalRecords = Number(headers['x-pagination-total-records'] || configs.length);

    const limit = Number(headers['x-pagination-limit'] || 10);
    const selectedIds = useMemo(() => Array.from(selected), [selected]);
    const selectedNames = useMemo(
        () => configs.filter((c) => selected.has(c.applicationId)).map((c) => c.applicationName),
        [configs, selected]);

    const isSingle = selected.size === 1;

    // For a single selected business we render its real config (per-entity reports, per-report cadence,
    // window, real prices). For multiple, the report-type list with a fresh (empty) selection.
    const singleReports = useMemo(() => {
        if (!isSingle || !singleConfig || !Array.isArray(singleConfig.reports)) return null;
        return singleConfig.reports.map((r) => ({
            key: singleReportKey(r),
            reportType: r.reportType,
            label: singleReportLabel(r),
            credits: r.creditsCost || 0,
            available: !!r.available,
            reason: r.message,
            entityId: r.entityId,
        }));
    }, [isSingle, singleConfig]);

    const singleInitialSelections = useMemo(() => {
        if (!singleConfig || !Array.isArray(singleConfig.reports)) return {};
        const sel = {};
        singleConfig.reports.forEach((r) => {
            if (!r.enabled) return;
            const seed = r.frequency || singleConfig.frequency;
            sel[singleReportKey(r)] = KNOWN_CADENCES.includes(seed) ? seed : 'EVERY_1_MONTH';
        });
        return sel;
    }, [singleConfig]);

    // Run a server query preserving the active filters. `enabled` maps the status control to the backend param.
    const runQuery = (pagination = {}) => {
        if (!onQuery) return;
        const filters = {};
        if (search.trim()) filters.search = search.trim();
        if (status !== 'ALL') filters.enabled = status === 'ON';
        onQuery(pagination, filters);
    };

    // Learn once whether the client is on the credit system, so the list can hide all credit wording (the
    // "Credits / cycle" column and the cost subtitle) for non-credit clients. The preview endpoint returns the
    // client's balance regardless of selection; a null balance means "not a credit client".
    useEffect(() => {
        if (!onPreview) return undefined;
        let cancelled = false;
        Promise.resolve(onPreview({ applicationIds: [], reports: [], startDate: null, endDate: null }))
            .then((res) => { if (!cancelled && res) setCreditsApplicable(typeof res.balance === 'number'); })
            .catch(() => { });
        return () => { cancelled = true; };
    }, [onPreview]);

    // Is the dev "run now" tool available in this environment? Fails soft (endpoint off/absent in prod).
    useEffect(() => {
        if (!getDevEnabled) return;
        Promise.resolve(getDevEnabled())
            .then((v) => setDevEnabled(v === true))
            .catch(() => setDevEnabled(false));
    }, [getDevEnabled]);

    // Dev/local-only: fire monitoring immediately for the one selected business and report how many alerts fired.
    const handleRunNow = async () => {
        if (readOnly || !onRunNow || !isSingle || running) return;
        setRunning(true);
        setRunMsg(null);
        try {
            const alerts = await onRunNow(selectedIds[0]);
            const n = Array.isArray(alerts) ? alerts.filter(Boolean).length : 0;
            setRunMsg(n === 0 ? 'Ran — no changes detected' : `Ran — ${n} alert${n === 1 ? '' : 's'} created (see Alerts)`);
        } catch (e) {
            setRunMsg('Run failed');
        } finally {
            setRunning(false);
        }
    };

    // Debounced search + status filter → reload from page 0 (skip the very first mount).
    const didMount = React.useRef(false);
    useEffect(() => {
        if (!didMount.current) { didMount.current = true; return undefined; }
        const t = setTimeout(() => runQuery({ page: 0, limit }), 350);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, status]);

    // Row selection (checkbox), tracked by applicationId so it persists across pages/filters. Read-only
    // users get no selection at all, which also removes the schedule-apply toolbar (gated on a selection) —
    // matching how the rest of monitoring hides edits for read-only users.
    const selectRow = readOnly ? undefined : {
        mode: 'checkbox',
        clickToSelect: true,
        selected: selectedIds,
        onSelect: (row, isSelect) => setSelected((prev) => {
            const next = new Set(prev);
            if (isSelect) next.add(row.applicationId); else next.delete(row.applicationId);
            return next;
        }),
        onSelectAll: (isSelect, rows) => setSelected((prev) => {
            const next = new Set(prev);
            rows.forEach((r) => { if (isSelect) next.add(r.applicationId); else next.delete(r.applicationId); });
            return next;
        }),
    };

    // Columns auto-size and the table fills the card's full width, matching the other list tables. The
    // "Credits / cycle" column is only shown to credit clients — non-credit clients see no cost anywhere.
    const columns = [
        { dataField: 'applicationName', text: 'Business', sort: false },
        {
            dataField: 'monitoringEnabled', text: 'Status', sort: false,
            formatter: (cell) => (
                <span className={`bulkSetup__status ${cell ? 'is-on' : 'is-off'}`}>{cell ? 'Monitored' : 'Off'}</span>
            ),
        },
        {
            dataField: 'nextSweep', text: 'Next sweep', sort: false,
            formatter: (cell) => (cell ? moment(cell).format('MMM DD, YYYY') : '—'),
        },
        {
            dataField: 'monitoringReports', text: 'Reports', sort: false,
            formatter: (cell) => (Array.isArray(cell) && cell.length
                ? <span className="bulkSetup__reportPills">{cell.map((r) => <span key={r} className="bulkSetup__pill">{reportLabel(r)}</span>)}</span>
                : '—'),
        },
    ];
    if (creditsApplicable) {
        columns.push({
            dataField: 'creditsPerCycle', text: 'Credits / cycle', sort: false,
            formatter: (cell, row) => (row.monitoringEnabled ? cell : 0),
        });
    }

    // On open: for one business, load its full config (source of truth); for many, just fetch the balance
    // (the per-report cost is computed client-side inside MonitoringSetup).
    useEffect(() => {
        if (!dialogOpen || !selectedIds.length) return undefined;
        let cancelled = false;
        if (isSingle && getConfig) {
            setLoadingConfig(true);
            Promise.resolve(getConfig(selectedIds[0]))
                .then((res) => {
                    if (cancelled) return;
                    const cfg = res && res.response ? res.response : res;
                    setSingleConfig(cfg || null);
                    if (cfg && typeof cfg.balance === 'number') setBalance(cfg.balance);
                })
                .catch(() => {
                    if (cancelled) return;
                    // Surface the failure instead of silently rendering an empty setup once loading clears.
                    setSingleConfig(null);
                    setApplyError('Could not load monitoring configuration. Please close and try again.');
                })
                .finally(() => { if (!cancelled) setLoadingConfig(false); });
        } else if (onPreview) {
            Promise.resolve(onPreview({ applicationIds: selectedIds, reports: [], startDate: null, endDate: null }))
                .then((res) => { if (!cancelled && res && typeof res.balance === 'number') setBalance(res.balance); })
                .catch(() => { });
        }
        return () => { cancelled = true; };
    }, [dialogOpen, isSingle, getConfig, onPreview, selectedIds]);

    const openDialog = () => { setBalance(null); setSingleConfig(null); setApplyError(null); setDialogOpen(true); };

    // Single business → per-app update (preserves per-entity reports + exact cadences). Many → bulk apply.
    const handlePrimary = async ({ selectedReports, startDate, endDate }) => {
        if (readOnly) return; // defensive: read-only users can't apply schedule changes
        setApplying(true);
        setApplyError(null);
        try {
            if (isSingle && updateConfig && singleReports) {
                const chosen = new Map(selectedReports.map((s) => [s.report.key, s.cadence]));
                const entityConfigurations = singleReports.map((r) => ({
                    reportType: r.reportType,
                    entityId: r.entityId,
                    enabled: chosen.has(r.key),
                    frequency: chosen.get(r.key) || null,
                }));
                await updateConfig(selectedIds[0], {
                    enabled: entityConfigurations.some((e) => e.enabled),
                    explicitWindow: true,
                    startDate,
                    endDate,
                    entityConfigurations,
                });
            } else if (onApply) {
                const res = await onApply({
                    applicationIds: selectedIds,
                    reports: selectedReports.map((s) => ({ reportType: s.report.reportType, frequency: s.cadence })),
                    startDate,
                    endDate,
                });
                // Nothing applied → keep the dialog open and show why instead of silently closing.
                if (res && res.appliedCount === 0 && res.skippedCount > 0) {
                    setApplyError(res.skipReason || 'Could not apply to the selected businesses.');
                    return;
                }
            }
            setDialogOpen(false);
            setSelected(new Set());
            // Reload through the child's own filtered query so the table still matches the active search /
            // status filters after applying (the parent's onApplied reloads unfiltered). Fall back to it
            // only when this component isn't driving the query.
            if (onQuery) runQuery({ page, limit });
            else if (onApplied) onApplied();
        } catch (e) {
            setApplyError('Something went wrong applying monitoring. Please try again.');
        } finally {
            setApplying(false);
        }
    };

    return (
        <div className="bulkSetup">
            <div className="bulkSetup__head">
                <div>
                    <h5 className="bulkSetup__title">Monitoring setup</h5>
                    <p className="bulkSetup__subtitle">
                        Select businesses and set up a monitoring schedule in bulk.
                        {creditsApplicable
                            ? ' Each enabled report is billed in credits per check — you’ll see the cost before applying.'
                            : ' Pick the reports and cadence for each business — you’ll review the schedule before applying.'}
                    </p>
                </div>
                {selected.size > 0 && (
                    <div className="bulkSetup__toolbar">
                        <span>{selected.size} selected</span>
                        <button className="bulkSetup__btn" onClick={openDialog}>Set up monitoring</button>
                        <button className="bulkSetup__btnGhost" onClick={() => setSelected(new Set())}>Clear</button>
                        {devEnabled && onRunNow && (
                            <>
                                <button
                                    className="bulkSetup__btnGhost bulkSetup__btnDev"
                                    onClick={handleRunNow}
                                    disabled={!isSingle || running}
                                    title={`Dev/local only — ${isSingle ? 'runs this business’s monitoring immediately and shows the alerts. Not available in production.' : 'select exactly one business to run.'}`}
                                >
                                    {running ? 'Running…' : 'Run now (dev)'}
                                </button>
                                {runMsg && <span className="bulkSetup__runMsg">{runMsg}</span>}
                            </>
                        )}
                    </div>
                )}
            </div>

            <div className="bulkSetup__filters">
                <input
                    className="bulkSetup__search"
                    type="text"
                    placeholder="Search businesses…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <select className="bulkSetup__statusSelect" value={status} onChange={(e) => setStatus(e.target.value)}>
                    <option value="ALL">All statuses</option>
                    <option value="ON">Monitored</option>
                    <option value="OFF">Off</option>
                </select>
            </div>

            <div className="bulkSetup__tableWrap">
                <TableComponent
                    className="custom-table"
                    data={configs}
                    keyField="applicationId"
                    columns={columns}
                    sort={true}
                    selectRow={selectRow}
                    totalRecords={totalRecords}
                    totalPages={totalPages}
                    currentPage={page}
                    onChangePagination={runQuery}
                    loading={false}
                />
            </div>

            {dialogOpen && (
                <div className="bulkSetup__backdrop" onClick={() => !applying && setDialogOpen(false)}>
                    <div className="bulkSetup__dialog" onClick={(e) => e.stopPropagation()}>
                        {applyError && <div className="bulkSetup__applyError" role="alert">{applyError}</div>}
                        {isSingle && loadingConfig ? (
                            <div className="bulkSetup__dialogLoading">Loading configuration…</div>
                        ) : (
                            <MonitoringSetup
                                mode={isSingle ? 'single' : 'bulk'}
                                reports={isSingle ? (singleReports || []) : BULK_REPORTS}
                                balance={balance}
                                businessCount={selected.size}
                                businesses={selectedNames}
                                initialSelections={isSingle ? singleInitialSelections : {}}
                                initialStart={isSingle && singleConfig?.startDate ? moment(singleConfig.startDate).format('YYYY-MM-DD') : null}
                                initialEnd={isSingle && singleConfig?.endDate ? moment(singleConfig.endDate).format('YYYY-MM-DD') : null}
                                initialOngoing={isSingle ? !singleConfig?.endDate : true}
                                busy={applying}
                                onPrimary={handlePrimary}
                                onCancel={() => setDialogOpen(false)}
                            />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MonitoringBulkSetup;
